import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of as observableOf } from 'rxjs';
import { BaseComponent } from 'src/app/base/base.component';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { IngestTreeNode } from 'src/app/messaging/ingestFile';
import { formatBytes } from 'src/app/base/formatBytes';

/** Flat node for the mat-tree flattener - mirrors the FileFlatNode recipe
 * already used by folder-browser-dialog/file-browser-dialog, keyed by
 * root-relative path instead of an absolute one. */
class IngestFlatNode {
  constructor(
    public expandable: boolean,
    public relativePath: string,
    public name: string,
    public level: number,
    public isFolder: boolean,
    public sizeBytes?: number) { }
}

@Component({
    selector: 'app-ingest-file-tree',
    templateUrl: './ingest-file-tree.component.html',
    styleUrls: ['./ingest-file-tree.component.css'],
    standalone: false
})
export class IngestFileTreeComponent extends BaseComponent implements OnInit {

  treeControl: FlatTreeControl<IngestFlatNode>;
  treeFlattener: MatTreeFlattener<IngestTreeNode, IngestFlatNode>;
  dataSource: MatTreeFlatDataSource<IngestTreeNode, IngestFlatNode>;
  isLoading = false;
  formatBytes = formatBytes;

  private hasLoadedOnce = false;

  constructor(
    private configService: ConfigService,
    private cdRef: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService) {
    super(errorLogger, matDialog);

    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel, this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<IngestFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  transformer = (node: IngestTreeNode, level: number): IngestFlatNode =>
    new IngestFlatNode(node.isFolder, node.relativePath, node.name, level, node.isFolder, node.sizeBytes);

  private _getLevel = (node: IngestFlatNode) => node.level;
  private _isExpandable = (node: IngestFlatNode) => node.expandable;
  private _getChildren = (node: IngestTreeNode): Observable<IngestTreeNode[]> => observableOf(node.children ?? []);

  hasChild = (_: number, node: IngestFlatNode) => node.expandable;

  /** Reloads the tree from the server. Expand/collapse state is preserved by
   * relativePath (not node identity, which is rebuilt on every load) - every
   * currently-expanded path is captured first and re-applied once the new
   * flat node list exists, rather than calling treeControl.expandAll(). */
  async refresh(): Promise<void> {
    this.isLoading = true;

    const expandedPaths = new Set<string>(
      (this.treeControl.dataNodes ?? [])
        .filter(n => this.treeControl.isExpanded(n))
        .map(n => n.relativePath)
    );
    if (!this.hasLoadedOnce) {
      expandedPaths.add(''); // root expanded by default on first load only
    }

    try {
      const tree = await this.configService.getIngestTreeAsync();
      this.dataSource.data = [tree];
      this.hasLoadedOnce = true;

      for (const node of this.treeControl.dataNodes) {
        if (expandedPaths.has(node.relativePath)) {
          this.treeControl.expand(node);
        }
      }
      this.cdRef.detectChanges();
    } catch (err) {
      this.displayError('Failed to load the ingest file tree', 'Error', err);
    } finally {
      this.isLoading = false;
    }
  }

  onDeleteClicked(node: IngestFlatNode): void {
    this.showConfirmDialog(
      'Delete File',
      `Are you sure you want to delete "${node.name}"? This cannot be undone.`,
      async (confirmed) => {
        if (!confirmed) { return; }
        try {
          const success = await this.configService.deleteIngestFileAsync(node.relativePath);
          if (success) {
            this.snackBar.open(`Deleted "${node.name}".`, 'Close', { duration: 4000 });
            await this.refresh();
          } else {
            this.snackBar.open(`Failed to delete "${node.name}".`, 'Close', { duration: 4000, panelClass: 'snackbar-error' });
          }
        } catch (err) {
          this.displayError(`Failed to delete "${node.name}"`, 'Error', err);
        }
      },
      'Delete');
  }
}
